import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import Swal from 'sweetalert2';
import { useAuth } from '../../utils';
import { useNavigate } from 'react-router-dom';

const LoginForm = () => {
  const [stayLoggedIn, setStayLoggedIn] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const [showHoldHint, setShowHoldHint] = useState(false);
  const [holdProgress, setHoldProgress] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();
  const holdTimerRef = useRef(null);
  const holdHintTimerRef = useRef(null);
  const holdIntervalRef = useRef(null);
  const holdStartedAtRef = useRef(0);
  const longPressTriggeredRef = useRef(false);

  const handleCheckboxChange = () => {
    setStayLoggedIn(!stayLoggedIn);
  };

  const clearHold = () => {
    clearTimeout(holdTimerRef.current);
    clearTimeout(holdHintTimerRef.current);
    clearInterval(holdIntervalRef.current);
    holdTimerRef.current = null;
    holdHintTimerRef.current = null;
    holdIntervalRef.current = null;
    holdStartedAtRef.current = 0;
    setIsHolding(false);
    setHoldProgress(0);
  };

  const startHold = (event) => {
    if (isLoggingIn) return;

    const form = event.currentTarget.form;
    const username = form?.username?.value?.trim();
    const password = form?.password?.value;
    if (!username || !password) return;

    longPressTriggeredRef.current = false;
    holdStartedAtRef.current = Date.now();
    setIsHolding(true);
    setShowHoldHint(false);
    setHoldProgress(0);

    holdHintTimerRef.current = setTimeout(() => {
      setShowHoldHint(true);
      holdIntervalRef.current = setInterval(() => {
        const visualElapsed = Date.now() - holdStartedAtRef.current - 1000;
        const progress = Math.max(0, Math.min(visualElapsed / 6000, 1));
        setHoldProgress(progress);
      }, 50);
    }, 1000);

    holdTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      clearHold();
      setIsLoggingIn(true);
      login(username, password, stayLoggedIn)
        .then(({ success }) => {
          if (success) {
            navigate('/docs');
            return;
          }

          Swal.fire({
            title: 'Unable to open Docs',
            text: 'Please check your username and password.',
            icon: 'warning',
            confirmButtonText: 'Try again',
            confirmButtonColor: '#2878c8',
          });
        })
        .catch(() => {
          Swal.fire({
            title: 'Unable to open Docs',
            text: 'Please try again in a moment.',
            icon: 'error',
            confirmButtonText: 'Try again',
            confirmButtonColor: '#2878c8',
          });
        })
        .finally(() => setIsLoggingIn(false));
    }, 7000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }

    const username = e.target.username.value;
    const password = e.target.password.value;

    setIsLoggingIn(true);
    try {
      const { success } = await login(username, password, stayLoggedIn);

      if (success) {
        navigate("/board");
      } else {
        Swal.fire({
          title: "Login failed",
          text: "Please check your username and password.",
          icon: "warning",
          confirmButtonText: "Try again",
          confirmButtonColor: '#2878c8',
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Unable to sign in",
        text: "Please try again in a moment.",
        icon: "error",
        confirmButtonText: "Try again",
        confirmButtonColor: '#2878c8',
      });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleButtonClick = (event) => {
    if (longPressTriggeredRef.current) {
      event.preventDefault();
      longPressTriggeredRef.current = false;
      return;
    }
    clearHold();
  };

  return (
    <>
      {isLoggingIn && createPortal(
        <LoadingOverlay role="status" aria-live="polite">
          <LoadingCard>
            <LoadingMark>
              <LoadingOrbit />
              <LoadingDot />
            </LoadingMark>
            <LoadingEyebrow>SECURE ACCESS</LoadingEyebrow>
            <LoadingTitle>Signing you in</LoadingTitle>
            <LoadingText>Checking your account details...</LoadingText>
            <LoadingTrack><LoadingTrackFill /></LoadingTrack>
          </LoadingCard>
        </LoadingOverlay>,
        document.body
      )}

      <Form id="login-form" onSubmit={handleSubmit}>
      <input type="text" name="username" placeholder="Username" required />
      <input type="password" name="password" placeholder="Password" required />
      <Label>
        <span className="custom-checkbox-wrapper">
          <input type="checkbox" onChange={handleCheckboxChange} checked={stayLoggedIn} />
          <span className="custom-checkbox"></span>
        </span>
        <span className="custom-checkbox-text">Stay logged in</span>
      </Label>
        <LoginButton
          type="submit"
          disabled={isLoggingIn}
          onMouseDown={startHold}
          onMouseUp={clearHold}
          onMouseLeave={clearHold}
          onTouchStart={startHold}
          onTouchEnd={clearHold}
          onTouchCancel={clearHold}
          onClick={handleButtonClick}
          onContextMenu={(event) => event.preventDefault()}
        >
          <HoldProgress style={{ transform: `scaleX(${holdProgress})` }} />
          <ButtonContent>
            {isLoggingIn ? <ButtonSpinner /> : null}
            <span>{isLoggingIn ? 'Signing in...' : 'Login'}</span>
          </ButtonContent>
        </LoginButton>
        {isHolding && showHoldHint && <HoldHint>Keep holding...</HoldHint>}
      </Form>
    </>
  );
};

export default LoginForm;

const Form = styled.form`
  width: 100%;
  margin: 0;
  display: grid;
  gap: 16px;

  > input,
  > button {
    width: 100%;
    height: 56px;
    border-radius: 6px;
    border: 0;
  }

  > input {
    color: rgba(255, 255, 255, 0.96);
    background: rgba(255, 255, 255, 0.08);
    font-family: inherit;
    font-size: 16px;
    padding: 0 16px;
  }

  > input:focus {
    outline: 2px solid #3399ff;
  }

  > input::placeholder {
    color: rgba(255, 255, 255, 0.38);
  }

  > button {
    cursor: pointer;
    padding: 0 16px;
    background: #3399ff;
    color: #f9f9f9;
    border: 0;
  }
`;

const LoginButton = styled.button`
  position: relative;
  overflow: hidden;
  cursor: pointer;
  padding: 0 16px;
  background: #3399ff;
  color: #f9f9f9;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    background: #278be9;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.82;
  }
`;

const ButtonContent = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const HoldProgress = styled.span`
  position: absolute;
  inset: 0;
  transform-origin: left center;
  background: rgba(10, 49, 94, 0.28);
  transition: transform 0.05s linear;
`;

const HoldHint = styled.span`
  margin-top: -8px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 12px;
  text-align: center;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(0.86); opacity: 0.58; }
  50% { transform: scale(1); opacity: 1; }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 24px;
  background: var(--app-modal-overlay, rgba(64, 64, 64, 0.72));
  backdrop-filter: blur(12px);
`;

const LoadingCard = styled.div`
  width: min(100%, 390px);
  padding: 38px 34px 32px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 18px;
  background: linear-gradient(145deg, rgba(34, 58, 86, 0.98), rgba(20, 35, 56, 0.98));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.32);
  text-align: center;

  [data-auth-theme="night"] & {
    background: var(--app-surface, #2b2b2b);
    border-color: var(--app-border, #555);
  }
`;

const LoadingMark = styled.div`
  position: relative;
  width: 58px;
  height: 58px;
  margin: 0 auto 24px;
`;

const LoadingOrbit = styled.div`
  width: 58px;
  height: 58px;
  border: 3px solid rgba(255, 255, 255, 0.18);
  border-top-color: #7bc4ff;
  border-right-color: #3399ff;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const LoadingDot = styled.div`
  position: absolute;
  top: 21px;
  left: 21px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #b9e2ff;
  animation: ${pulse} 1.2s ease-in-out infinite;
`;

const LoadingEyebrow = styled.div`
  color: #83caff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.8px;
`;

const LoadingTitle = styled.h4`
  margin: 10px 0 8px;
  color: #fff;
  font-size: 25px;
  font-weight: 500;
`;

const LoadingText = styled.p`
  margin: 0 0 24px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 14px;
`;

const LoadingTrack = styled.div`
  height: 4px;
  overflow: hidden;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.12);
`;

const LoadingTrackFill = styled.div`
  width: 42%;
  height: 100%;
  border-radius: inherit;
  background: #69baff;
  animation: loading-slide 1.35s ease-in-out infinite;

  @keyframes loading-slide {
    0% { transform: translateX(-120%); }
    100% { transform: translateX(300%); }
  }
`;

const ButtonSpinner = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.75s linear infinite;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 6px 0 8px;
  color: rgba(255, 255, 255, 0.5);

  .custom-checkbox-wrapper {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    input {
      opacity: 0;
      position: absolute;
      width: 100%;
      height: 100%;
      cursor: pointer;
    }

    .custom-checkbox {
      display: block;
      width: 32px;
      height: 32px;
      border: 3px solid rgba(255, 255, 255, 0.18);
      border-radius: 6px;
      transition: all 0.375s;
    }
  }

  .custom-checkbox-wrapper input:checked + .custom-checkbox {
    transform: rotate(45deg);
    width: 14px;
    margin-left: 12px;
    margin-top: -2px;
    border-color: #3399FF;
    border-top-color: transparent;
    border-left-color: transparent;
    border-radius: 0;
  }
`;
